import { useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, XCircle } from 'lucide-react'
import { EmptyState } from '../ui/EmptyState'
import { SignalRowSkeleton } from '../ui/Skeleton'
import { useAlerts, useRetryAlert } from '../../hooks/useCompetitiveData'
import type { AlertRow } from '../../types/competitive'

type AlertDateRange = '7d' | '30d' | 'all'

function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'No updates yet'
  }

  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function severityClasses(severity: string): string {
  const normalized = severity.toLowerCase()
  if (normalized === 'critical') {
    return 'text-red-600 bg-red-50 border border-red-200'
  }
  if (normalized === 'high') {
    return 'text-orange-700 bg-orange-50 border border-orange-200'
  }
  if (normalized === 'medium') {
    return 'text-amber-700 bg-amber-50 border border-amber-200'
  }
  return 'text-slate-700 bg-slate-100 border border-slate-200'
}

function dateRangeMatches(sentAt: string | null, range: AlertDateRange): boolean {
  if (range === 'all') {
    return true
  }

  if (!sentAt) {
    return false
  }

  const sentMs = new Date(sentAt).getTime()
  if (Number.isNaN(sentMs)) {
    return false
  }

  const now = Date.now()
  const windowMs = range === '7d' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000
  return sentMs >= now - windowMs
}

function tierMatches(tier: string, filter: string): boolean {
  const normalized = tier.toLowerCase()
  if (filter === 'all') {
    return true
  }
  if (filter === 'low') {
    return normalized !== 'critical' && normalized !== 'high'
  }
  return normalized === filter
}

export default function AlertLogTab() {
  const [tierFilter, setTierFilter] = useState('all')
  const [competitorSearch, setCompetitorSearch] = useState('')
  const [dateRange, setDateRange] = useState<AlertDateRange>('30d')
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({})
  const [expandedBodies, setExpandedBodies] = useState<Record<number, boolean>>({})

  const { data, isLoading, error } = useAlerts(500)
  const retryAlertMutation = useRetryAlert()

  const alerts: AlertRow[] = data?.alerts ?? []
  const alertsError = error instanceof Error ? error.message : null

  const filteredAlerts = useMemo(() => {
    const query = competitorSearch.trim().toLowerCase()

    return alerts.filter((alert) => {
      if (!tierMatches(alert.alert_tier, tierFilter)) {
        return false
      }

      if (!dateRangeMatches(alert.sent_at, dateRange)) {
        return false
      }

      if (query) {
        const competitor = (alert.competitor_name || '').toLowerCase()
        if (!competitor.includes(query)) {
          return false
        }
      }

      return true
    })
  }, [alerts, competitorSearch, dateRange, tierFilter])

  const toggleExpandedRow = (alertId: number) => {
    setExpandedRows((current) => ({ ...current, [alertId]: !current[alertId] }))
  }

  const toggleExpandedBody = (alertId: number) => {
    setExpandedBodies((current) => ({ ...current, [alertId]: !current[alertId] }))
  }

  const retryError = retryAlertMutation.error instanceof Error ? retryAlertMutation.error.message : null

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-slate-500" />
        <h2 className="text-lg font-semibold text-slate-900">Alert Log</h2>
      </div>

      <div className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 md:grid-cols-3">
        <select
          value={tierFilter}
          onChange={(event) => setTierFilter(event.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          <option value="all">All tiers</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="low">Low</option>
        </select>

        <input
          type="text"
          value={competitorSearch}
          onChange={(event) => setCompetitorSearch(event.target.value)}
          placeholder="Search competitor name"
          className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        />

        <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setDateRange('7d')}
            className={`px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              dateRange === '7d' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            Last 7 days
          </button>
          <button
            type="button"
            onClick={() => setDateRange('30d')}
            className={`px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              dateRange === '30d' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            Last 30 days
          </button>
          <button
            type="button"
            onClick={() => setDateRange('all')}
            className={`px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              dateRange === 'all' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100'
            }`}
          >
            All time
          </button>
        </div>
      </div>

      {alertsError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{alertsError}</div>
      )}

      {retryError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{retryError}</div>
      )}

      <div className="space-y-2">
        {isLoading ? (
          <SignalRowSkeleton />
        ) : filteredAlerts.length === 0 ? (
          <EmptyState
            icon={<AlertTriangle className="h-6 w-6" />}
            title="No alerts sent yet"
            description="Alert events will appear here when competitor thresholds are crossed."
          />
        ) : (
          filteredAlerts.map((alert) => {
            const expanded = Boolean(expandedRows[alert.id])
            const bodyExpanded = Boolean(expandedBodies[alert.id])
            const bodyText = (alert.body || '').trim() || 'No message body.'
            const bodyNeedsClamp = bodyText.length > 220
            const status = (alert.delivery_status || '').toLowerCase()
            const isRetrying = retryAlertMutation.isPending && retryAlertMutation.variables === alert.id

            return (
              <div key={alert.id} className="rounded-md border border-slate-200 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-md px-2 py-0.5 text-xs font-semibold ${severityClasses(alert.alert_tier)}`}
                      >
                        {alert.alert_tier}
                      </span>
                      <span className="text-sm font-medium text-slate-800">
                        {alert.competitor_name || 'Daily Digest'}
                      </span>
                    </div>

                    <div className="text-sm text-slate-700">{alert.subject || 'No subject'}</div>

                    <div className="flex flex-wrap items-center gap-2">
                      {status === 'sent' ? (
                        <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          Sent
                        </span>
                      ) : status === 'failed' ? (
                        <span className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                          <XCircle className="h-4 w-4 text-red-500" />
                          Failed
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 capitalize">
                          {status || 'unknown'}
                        </span>
                      )}

                      {status === 'failed' ? (
                        <button
                          type="button"
                          onClick={() => retryAlertMutation.mutate(alert.id)}
                          disabled={isRetrying}
                          className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
                        >
                          {isRetrying ? 'Retrying...' : 'Retry'}
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="text-xs text-slate-500">{formatTimestamp(alert.sent_at)}</div>
                    <button
                      type="button"
                      onClick={() => toggleExpandedRow(alert.id)}
                      className="rounded-md border border-slate-200 bg-white p-1 text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      aria-label={expanded ? 'Collapse alert body' : 'Expand alert body'}
                    >
                      {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {expanded ? (
                  <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <div
                      style={
                        !bodyExpanded && bodyNeedsClamp
                          ? {
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }
                          : undefined
                      }
                    >
                      {bodyText}
                    </div>

                    {bodyNeedsClamp ? (
                      <button
                        type="button"
                        onClick={() => toggleExpandedBody(alert.id)}
                        className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        {bodyExpanded ? 'show less' : 'show more'}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })
        )}
      </div>
    </section>
  )
}
